import React, { useEffect, useState } from 'react';
import './OrbitPaymentSplit.css';

const nonNegative = value => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
const percentage = value => Number.isFinite(Number(value)) ? Math.min(100, Math.max(0, Number(value))) : 0;
export const disabledOrbitSplit = { cashPercentage: 0, creditPercentage: 100, cashAmount: 0, creditAmount: 0, isEnabled: false };

// Uses the same parent payment contract as the existing templates and checkout.
export default function OrbitPaymentSplit({ cashTotal, creditTotal, paymentSplitData, setPaymentSplitData, userSettings, disabled = false }) {
  const allowed = userSettings?.priceSplit?.enabled !== false && typeof setPaymentSplitData === 'function';
  const enabled = allowed && Boolean(paymentSplitData?.isEnabled);
  const cash = nonNegative(cashTotal);
  const credit = nonNegative(creditTotal);
  const pct = percentage(paymentSplitData?.cashPercentage);
  const cashAmount = cash * pct / 100;
  const creditAmount = credit * (100 - pct) / 100;
  const [cashInput, setCashInput] = useState(cashAmount.toFixed(2));
  const [creditInput, setCreditInput] = useState(creditAmount.toFixed(2));
  const [percentageInput, setPercentageInput] = useState(String(pct));
  const makeSplit = value => {
    const next = percentage(value);
    return { cashPercentage: next, creditPercentage: 100 - next, cashAmount: cash * next / 100, creditAmount: credit * (100 - next) / 100, isEnabled: true };
  };
  const update = value => {
    const next = makeSplit(value);
    setPaymentSplitData(next);
    setCashInput(next.cashAmount.toFixed(2));
    setCreditInput(next.creditAmount.toFixed(2));
    setPercentageInput(String(next.cashPercentage));
  };
  useEffect(() => {
    setCashInput(cashAmount.toFixed(2));
    setCreditInput(creditAmount.toFixed(2));
    setPercentageInput(String(pct));
  }, [cashAmount, creditAmount, pct]);
  useEffect(() => {
    if (!enabled) return;
    if (paymentSplitData.cashPercentage !== pct || paymentSplitData.creditPercentage !== 100 - pct ||
        paymentSplitData.cashAmount !== cashAmount || paymentSplitData.creditAmount !== creditAmount) {
      setPaymentSplitData({ cashPercentage: pct, creditPercentage: 100 - pct, cashAmount, creditAmount, isEnabled: true });
    }
  }, [enabled, pct, cashAmount, creditAmount, paymentSplitData, setPaymentSplitData]);
  if (!allowed) return null;
  const currency = userSettings?.currency || 'USD';
  const money = value => {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value); }
    catch (_) { return `${userSettings?.custom_currency_symbol || currency} ${value.toFixed(2)}`; }
  };
  return <section className="orbit-payment-split" aria-label="Split payment">
    <label className="orbit-payment-split-toggle"><input type="checkbox" checked={enabled} disabled={disabled} onChange={event => {
      if (event.target.checked) update(paymentSplitData?.isEnabled && Number.isFinite(Number(paymentSplitData.cashPercentage)) ? pct : 50);
      else setPaymentSplitData({ ...disabledOrbitSplit });
    }} /><span>Split between cash and credit</span></label>
    {enabled && <fieldset disabled={disabled}>
      <legend>Choose your payment split</legend>
      <p>Split your whole buylist, or turn this off to use each card’s payment choice.</p>
      <input aria-label="Cash percentage slider" type="range" min="0" max="100" step="0.1" value={pct} onChange={event => update(event.target.value)} />
      <div className="orbit-payment-split-fields">
        <label>Cash percentage<input type="number" min="0" max="100" step="0.1" value={percentageInput} onChange={event => {
          setPercentageInput(event.target.value);
          if (event.target.value !== '' && Number.isFinite(Number(event.target.value))) update(event.target.value);
        }} onBlur={() => update(percentageInput)} /></label>
        <label>Cash amount<input type="number" min="0" max={cash} step="0.01" value={cashInput} onChange={event => setCashInput(event.target.value)} onBlur={() => update(cash > 0 ? nonNegative(cashInput) / cash * 100 : pct)} /></label>
        <label>Credit amount<input type="number" min="0" max={credit} step="0.01" value={creditInput} onChange={event => setCreditInput(event.target.value)} onBlur={() => update(credit > 0 ? 100 - nonNegative(creditInput) / credit * 100 : pct)} /></label>
      </div>
      <div className="orbit-payment-split-result" aria-live="polite"><span>Cash <strong>{money(cashAmount)}</strong></span><span>Store credit <strong>{money(creditAmount)}</strong></span></div>
    </fieldset>}
  </section>;
}
